import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import PlanExpiredBanner from '../../components/billing/PlanExpiredBanner';
import { authService, sessionService, clientService } from '../../services';
import { trainerService } from '../../services';
import api from '../../services/api';
import { MAX_UPLOAD_MB } from '../../utils/uploadLimits';

/**
 * Trainer Create Session Page — for solo trainers (individual orgs).
 *
 * Mirrors the 3-step UX of the client CreateSessionPage exactly so trainers
 * and clients see the same flow:
 *   Step 1: Basic Info (name, description, type, dates, times)
 *   Step 2: Participants (capacity, overall feedback, workbooks)
 *           — no trainer picker; backend auto-assigns the logged-in trainer
 *   Step 3: Pre & Post Session (Phase 2 config)
 */

const TrainerCreateSessionPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  // Effective per-session participant cap from the API (honours any admin
  // override); null until loaded, then used in place of the static plan map.
  const [effectiveLimit, setEffectiveLimit] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdSession, setCreatedSession] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sessionType: 'public',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timezone: 'Asia/Kolkata',
    expectedParticipants: 30,
    maxParticipants: 30,
    workbooks: [],
    enableOverallFeedback: true,

    // Phase 2: pre-session config
    preSessionEnabled: false,
    preSessionMaterials: [],
    selfAssessmentEnabled: false,
    selfAssessmentQuestions: [],
    challengeCaptureEnabled: true,
    expectationCaptureEnabled: true,

    // Phase 2: post-session config
    postSessionEnabled: false,
    actionPlanTrackingEnabled: true,
    managerReinforcementEnabled: true,
    reflectionCyclesEnabled: true,
    reflectionIntervals: [30, 60, 90],
    reflectionQuestions: [
      { questionText: 'What action did you implement?', order: 0 },
      { questionText: 'What result did you observe?', order: 1 },
      { questionText: 'What challenge remains?', order: 2 },
      { questionText: 'What support do you need?', order: 3 },
    ],
  });

  /**
   * Raw text for the reflection-intervals field, held separately from the parsed array.
   *
   * Rendering `reflectionIntervals.join(', ')` and reparsing on every keystroke destroyed
   * half-typed input: "30," split to ["30",""], the empty half became NaN, the filter
   * dropped it, and join() returned "30" — deleting the comma on the keypress that typed
   * it, so a second interval could never be entered.
   */
  const [reflectionIntervalsText, setReflectionIntervalsText] = useState('30, 60, 90');

  const [timeData, setTimeData] = useState({
    startHour: '09',
    startMinute: '00',
    startPeriod: 'AM',
    endHour: '10',
    endMinute: '00',
    endPeriod: 'AM',
  });

  const [errors, setErrors] = useState({});
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const checkIfDirty = () => {
    return formData.name !== '' ||
           formData.description !== '' ||
           formData.startDate !== '' ||
           formData.workbooks.length > 0;
  };

  const handleBackNavigation = (path) => {
    if (checkIfDirty()) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  const confirmLeave = () => {
    setShowBackConfirm(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const applyPlanLimits = (userData) => {
    const participantLimits = { free: 30, pro: 30, pro_plus: 100, enterprise_starter: 200, enterprise_scale: -1 };
    const userPlan = userData.primaryOrganization?.plan || userData.organization?.plan || 'free';
    const planLimit = participantLimits[userPlan] || 30;
    setFormData((prev) => ({
      ...prev,
      // maxParticipants is set authoritatively from the API (getSubscription),
      // which reflects any admin participant-cap override — don't clobber it here.
      expectedParticipants: planLimit === -1 ? 30 : Math.min(30, planLimit),
    }));
  };

  useEffect(() => {
    const cachedData = localStorage.getItem('user');
    if (cachedData) {
      const parsedUser = JSON.parse(cachedData);
      setUser(parsedUser);
      applyPlanLimits(parsedUser);
    }

    authService.getProfile().then((res) => {
      if (res.success && res.data) {
        const freshUser = res.data;
        setUser(freshUser);
        applyPlanLimits(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
      }
      if (res?.planStatus) setPlanStatus(res.planStatus);
    }).catch(() => {});

    trainerService.getDashboardStats().then((res) => {
      if (res?.success) setPlanStatus(res.data?.planStatus || res.planStatus || null);
    }).catch(() => {});

    // Authoritative participant cap from the backend (includes any admin
    // override). Overrides the static plan-map value once it resolves.
    clientService.getSubscription().then((res) => {
      const lim = res?.data?.features?.participants ?? res?.data?.usage?.participants?.limit;
      if (typeof lim === 'number' && lim !== 0) {
        setEffectiveLimit(lim);
        setFormData((prev) => ({ ...prev, maxParticipants: lim === -1 ? 500 : lim }));
      }
    }).catch(() => {});

    setFormData((prev) => ({
      ...prev,
      startTime: '09:00',
      endTime: '10:00',
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'maxParticipants') {
      const limit = getParticipantLimit();
      const num = Number(value);
      if (limit !== -1 && num > limit) {
        setFormData((prev) => ({ ...prev, [name]: limit }));
        setErrors((prev) => ({ ...prev, maxParticipants: `Maximum ${limit} for your plan` }));
        return;
      }
    }
    if (name === 'expectedParticipants') {
      const limit = getParticipantLimit();
      // The cap is the authority. maxParticipants is display-only and mirrors it,
      // but a plan-map seeder can land AFTER the real cap resolves and leave 30 in
      // form state — clamping to that told users "Cannot exceed 30" next to a
      // field reading 75.
      const clampTo = limit === -1 ? 500 : limit;
      const num = Number(value);
      if (num > clampTo) {
        setFormData((prev) => ({ ...prev, [name]: clampTo }));
        setErrors((prev) => ({ ...prev, expectedParticipants: `Cannot exceed ${clampTo}` }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    if (name === 'startDate' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setErrors((prev) => ({ ...prev, startDate: 'Cannot schedule session in the past' }));
      } else if (selectedDate.getTime() === today.getTime() && formData.startTime) {
        const [hours, mins] = formData.startTime.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          setErrors((prev) => ({ ...prev, startTime: 'Start time has already passed' }));
        } else {
          setErrors((prev) => ({ ...prev, startTime: '' }));
        }
      } else {
        setErrors((prev) => ({ ...prev, startTime: '' }));
      }
    }
  };

  const convertTo24Hour = (hour, minute, period) => {
    let h = parseInt(hour, 10);
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h = h + 12;
    }
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  const handleTimeChange = (field, value) => {
    const limitedValue = (field.includes('Hour') || field.includes('Minute'))
      ? value.toString().slice(0, 2)
      : value;
    const newTimeData = { ...timeData, [field]: limitedValue };
    setTimeData(newTimeData);

    const startTime24 = convertTo24Hour(
      field.startsWith('start') && field.includes('Hour') ? limitedValue : newTimeData.startHour,
      field.startsWith('start') && field.includes('Minute') ? limitedValue : newTimeData.startMinute,
      field === 'startPeriod' ? limitedValue : newTimeData.startPeriod
    );
    const endTime24 = convertTo24Hour(
      field.startsWith('end') && field.includes('Hour') ? limitedValue : newTimeData.endHour,
      field.startsWith('end') && field.includes('Minute') ? limitedValue : newTimeData.endMinute,
      field === 'endPeriod' ? limitedValue : newTimeData.endPeriod
    );

    setFormData((prev) => ({
      ...prev,
      startTime: startTime24,
      endTime: endTime24,
    }));

    setErrors((prev) => ({ ...prev, startTime: '', endTime: '' }));

    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate.getTime() === today.getTime()) {
        const [hours, mins] = startTime24.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          setErrors((prev) => ({ ...prev, startTime: 'Start time has already passed' }));
        }
      }
    }
  };

  const handleTimeBlur = (field, value) => {
    let formattedValue = value;

    if (field.includes('Hour')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) {
        formattedValue = '01';
      } else if (num > 12) {
        formattedValue = '12';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (field.includes('Minute')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 0) {
        formattedValue = '00';
      } else if (num > 59) {
        formattedValue = '59';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (formattedValue !== value) {
      handleTimeChange(field, formattedValue);
    }
  };

  // Step 1 only validates the basic info needed before moving on. Mirrors
  // client CreateSessionPage's validateStep1 so the UX feels identical.
  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Session name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';

    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.startDate = 'Cannot schedule session in the past';
      }

      if (formData.startTime && selectedDate.getTime() === today.getTime()) {
        const [hours, mins] = formData.startTime.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date(selectedDate);
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          newErrors.startTime = 'Start time has already passed';
        }
      }
    }

    if (formData.endDate && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        newErrors.endDate = 'End date cannot be before start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 2 — capacity & overall feedback. No trainer picker (auto-assigned to
  // the logged-in solo trainer by the backend), so the validation is shorter
  // than the client equivalent.
  const validateStep2 = () => {
    const newErrors = {};
    if (formData.expectedParticipants < 1) newErrors.expectedParticipants = 'Must be at least 1';
    const planLimit = getParticipantLimit();
    // Derive the cap here rather than reading formData.maxParticipants: an async
    // load effect can overwrite that with a stale plan-map value after the real
    // cap has resolved, which made a 50 under a 75 cap fail validation.
    const cap = planLimit === -1 ? 500 : planLimit;
    if (Number(formData.expectedParticipants) > cap) {
      newErrors.expectedParticipants = `Cannot exceed ${cap}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const getParticipantLimit = () => {
    // Prefer the API's effective cap (reflects any admin participant-cap override).
    if (typeof effectiveLimit === 'number' && effectiveLimit !== 0) return effectiveLimit;
    const participantLimits = { free: 30, pro: 30, pro_plus: 100, enterprise_starter: 200, enterprise_scale: -1 };
    const userPlan = user?.primaryOrganization?.plan || user?.organization?.plan || 'free';
    return participantLimits[userPlan] || 30;
  };

  const isUnlimitedParticipants = () => getParticipantLimit() === -1;

  const handleCreateSession = async () => {
    // Final guard — re-validate BOTH steps. Step 2 because the user may
    // have edited capacity after moving past it; step 1 because lingering
    // on step 2 long enough can push the original start time into the
    // past, and we don't continuously re-check from step 1 between steps.
    // If step 1 now fails, jump back so the error is attached to its field.
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }
    if (!validateStep2()) return;

    try {
      // Phase 2: pre-session config — undefined when disabled so the backend
      // schema defaults apply unchanged. Mirrors client CreateSessionPage.
      const preSessionConfig = formData.preSessionEnabled ? {
        enabled: true,
        materials: [], // uploaded AFTER session creation, then attached via update
        selfAssessment: {
          enabled: formData.selfAssessmentEnabled,
          questions: formData.selfAssessmentQuestions.map((q, idx) => ({
            questionText: q.questionText,
            questionType: q.questionType,
            options: q.optionsText !== undefined
              ? q.optionsText.split(',').map(s => s.trim()).filter(Boolean)
              : (q.options || []),
            order: idx,
          })),
        },
        challengeCapture: { enabled: formData.challengeCaptureEnabled },
        expectationCapture: { enabled: formData.expectationCaptureEnabled },
      } : undefined;

      const postSessionConfig = formData.postSessionEnabled ? {
        enabled: true,
        actionPlanTracking: { enabled: formData.actionPlanTrackingEnabled },
        managerReinforcement: { enabled: formData.managerReinforcementEnabled },
        reflectionCycles: {
          enabled: formData.reflectionCyclesEnabled,
          intervals: formData.reflectionIntervals,
          questions: formData.reflectionQuestions.map((q, idx) => ({
            questionText: q.questionText,
            order: idx,
          })),
        },
      } : undefined;

      const sessionData = {
        name: formData.name,
        description: formData.description,
        sessionType: formData.sessionType,
        scheduledDate: formData.startDate,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone,
        // No trainer field — backend auto-assigns the logged-in trainer
        expectedParticipants: formData.expectedParticipants,
        maxParticipants: isUnlimitedParticipants() ? 500 : getParticipantLimit(),
        workbooks: [],
        overallFeedback: { enabled: formData.enableOverallFeedback },
        preSession: preSessionConfig,
        postSession: postSessionConfig,
      };

      const response = await sessionService.createSession(sessionData);

      if (response.success) {
        const createdData = response.data;
        const sessionId = createdData._id;

        if (formData.workbooks.length > 0) {
          for (const wb of formData.workbooks) {
            if (wb.file) {
              try {
                await trainerService.addWorkbook(sessionId, wb.file);
              } catch (uploadError) {
                console.error('Error uploading workbook:', wb.name, uploadError);
              }
            }
          }
        }

        // Phase 2: upload pre-read materials and attach via session update.
        if (formData.preSessionEnabled && formData.preSessionMaterials.length > 0) {
          const uploadedMaterials = [];
          for (const material of formData.preSessionMaterials) {
            if (material.file) {
              try {
                const fd = new FormData();
                fd.append('file', material.file);
                const result = await api.upload(`/upload/session/${sessionId}/pre-read`, fd);
                if (result?.success && result.data) {
                  uploadedMaterials.push(result.data);
                } else {
                  console.error('[pre-read upload] unsuccessful response:', result);
                }
              } catch (err) {
                console.error('Error uploading pre-read material:', material.name, err);
              }
            }
          }
          if (uploadedMaterials.length > 0) {
            await sessionService.updateSession(sessionId, {
              preSession: { ...preSessionConfig, materials: uploadedMaterials },
            });
          }
        }

        const session = {
          id: sessionId,
          ...formData,
          sessionCode: createdData.code,
          qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${window.location.origin}/join/${createdData.code}`,
          status: createdData.status || 'scheduled',
          createdAt: createdData.createdAt,
        };

        setCreatedSession(session);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      alert(error.response?.data?.message || error.response?.data?.error || 'Failed to create session. Please try again.');
    }
  };

  // Workbook upload handlers
  const handleWorkbookUpload = (e) => {
    const files = Array.from(e.target.files);
    const tier = user?.primaryOrganization?.plan || user?.organization?.plan || 'free';
    const fileLimit = { free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[tier] || 2;

    if (formData.workbooks.length + files.length > fileLimit) {
      alert(`File limit reached. Maximum ${fileLimit} files allowed for your plan.`);
      return;
    }

    const newWorkbooks = files.map((file) => ({
      id: `workbook-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop() || 'unknown',
      previewUrl: URL.createObjectURL(file),
      file: file,
      uploadedAt: new Date().toISOString(),
    }));

    setFormData((prev) => ({
      ...prev,
      workbooks: [...prev.workbooks, ...newWorkbooks],
    }));

    e.target.value = '';
  };

  const removeWorkbook = (id) => {
    setFormData((prev) => ({
      ...prev,
      workbooks: prev.workbooks.filter((w) => w.id !== id),
    }));
  };

  const fileLimit = { free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.primaryOrganization?.plan || user?.organization?.plan || 'free'] || 2;

  // Hard gate — solo trainer plan expired.
  if (planStatus?.expired) {
    return (
      <DashboardLayout title="Create Session">
        <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
          <PlanExpiredBanner audience="solo-trainer" planEnd={planStatus.planEnd} />
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
            <h2 className="text-xl font-semibold text-neutral-900 mb-2">You can't create a session right now</h2>
            <p className="text-sm text-neutral-600 mb-6">
              Your plan has expired, so creating new sessions is paused.
            </p>
            <Button variant="primary" onClick={() => navigate('/trainer/billing')}>
              Go to Billing &amp; Renew
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Create Session">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
        {/* Back link */}
        <button
          type="button"
          onClick={() => handleBackNavigation('/trainer/sessions')}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back to Sessions</span>
        </button>

        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Create New Session</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Set up your training session step by step</p>
        </div>

        {/* Progress Steps */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-5">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => {
              const done = step < currentStep;
              const active = step === currentStep;
              return (
                <div key={step} className="flex items-center flex-1 min-w-0">
                  <div className="flex items-center min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all flex-shrink-0 ring-1 ring-inset ${
                        done
                          ? 'bg-emerald-500 text-white ring-emerald-500/40 shadow-[0_4px_12px_-4px_rgba(16,185,129,0.45)]'
                          : active
                          ? 'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-[0_4px_12px_-4px_rgba(255,138,0,0.35)]'
                          : 'bg-neutral-50 text-neutral-500 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-500 dark:ring-neutral-700'
                      }`}
                    >
                      {done ? <Check size={16} strokeWidth={2.5} /> : step}
                    </div>
                    <div className="ml-3 hidden sm:block min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        active
                          ? 'text-neutral-900 dark:text-neutral-100'
                          : done
                          ? 'text-neutral-700 dark:text-neutral-300'
                          : 'text-neutral-500 dark:text-neutral-500'
                      }`}>
                        {step === 1 ? 'Basic Info' : step === 2 ? 'Participants' : 'Pre & Post Session'}
                      </p>
                    </div>
                  </div>
                  {step < 3 && (
                    <div className={`flex-1 h-0.5 mx-3 sm:mx-4 rounded-full ${
                      done ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-neutral-800'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-semibold text-secondary-900 mb-2">Basic Information</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Enter the basic details about your training session</p>
                  </div>

                  <Input
                    label="Session Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder="Leadership Training - Day 1"
                    required
                    fullWidth
                  />

                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="A comprehensive training on leadership principles..."
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all resize-none placeholder:text-neutral-400"
                    />
                  </div>

                  {/* Session Type */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-3">Session Type</label>
                    <div className="space-y-3">
                      <div
                        onClick={() => handleChange({ target: { name: 'sessionType', value: 'public' } })}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.sessionType === 'public'
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              formData.sessionType === 'public' ? 'border-primary-600' : 'border-neutral-300'
                            }`}>
                              {formData.sessionType === 'public' && (
                                <div className="w-2.5 h-2.5 bg-primary-600 rounded-full"></div>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-secondary-900">Public</p>
                            <p className="text-xs sm:text-sm text-neutral-600 mt-0.5">Anyone with code can join</p>
                          </div>
                        </div>
                      </div>

                      <div className="relative p-4 border-2 rounded-lg border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5">
                            <div className="w-5 h-5 rounded-full border-2 border-neutral-300"></div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-secondary-900">Private</p>
                              <span className="px-2 py-0.5 bg-white border border-warning text-warning text-xs font-medium rounded-full">
                                Coming Soon
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-neutral-600 mt-0.5">Pre-registered participants only</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Start Date and End Date */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <Input
                      label="Start Date"
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      error={errors.startDate}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label={<>End Date <span className="text-neutral-400 text-xs font-normal">(optional)</span></>}
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange}
                        error={errors.endDate}
                        fullWidth
                        min={formData.startDate}
                      />
                      {!formData.endDate && formData.startDate && (
                        <p className="text-xs text-neutral-500 mt-1">Defaults to start date if not set</p>
                      )}
                    </div>
                  </div>

                  {/* Time Pickers */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-secondary-900 mb-2">
                        Start Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="1" max="12"
                          value={timeData.startHour}
                          onChange={(e) => handleTimeChange('startHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number" min="0" max="59"
                          value={timeData.startMinute}
                          onChange={(e) => handleTimeChange('startMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.startPeriod}
                          onChange={(e) => handleTimeChange('startPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.startTime && <p className="text-xs text-error mt-1">{errors.startTime}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-secondary-900 mb-2">
                        End Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number" min="1" max="12"
                          value={timeData.endHour}
                          onChange={(e) => handleTimeChange('endHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number" min="0" max="59"
                          value={timeData.endMinute}
                          onChange={(e) => handleTimeChange('endMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.endPeriod}
                          onChange={(e) => handleTimeChange('endPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.endTime && <p className="text-xs text-error mt-1">{errors.endTime}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-900 mb-2">Time Zone</label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 pr-10 text-sm text-secondary-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600 transition-all appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                    >
                      <option value="Asia/Kolkata">IST (UTC+05:30) India</option>
                    </select>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-neutral-200">
                    <Button variant="primary-filled" size="lg" onClick={handleNext}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      Next: Participants
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Participants */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-semibold text-secondary-900 mb-2">Participants</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Set participant capacity and session feedback. You're automatically assigned as the trainer.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Expected Participants"
                      name="expectedParticipants"
                      type="number"
                      value={formData.expectedParticipants}
                      onChange={handleChange}
                      error={errors.expectedParticipants}
                      min={1}
                      max={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label="Maximum Capacity"
                        name="maxParticipants"
                        type="number"
                        value={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                        readOnly
                        disabled
                        fullWidth
                      />
                      <p className="text-xs text-neutral-500 mt-1.5">
                        {isUnlimitedParticipants() ? 'Unlimited as per your plan' : `Fixed at ${getParticipantLimit()} as per your plan`}
                      </p>
                    </div>
                  </div>

                  {/* Overall Session Feedback Toggle */}
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-secondary-900">Overall Session Feedback</label>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          Collect feedback at the end of the entire session (after all subsessions)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, enableOverallFeedback: !prev.enableOverallFeedback }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          formData.enableOverallFeedback ? 'bg-primary-600' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.enableOverallFeedback ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {formData.enableOverallFeedback && (
                      <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs sm:text-sm text-primary-800">
                            Participants will be asked to rate the overall session and provide feedback after all subsessions are completed.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Workbook Upload */}
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-900">Workbooks (Optional)</label>
                        <p className="text-xs text-neutral-500 mt-0.5">Upload training materials for participants</p>
                        {/* nginx rejects an oversized upload before the API sees it, so the
                            failure reads as a generic server error — state the limit here. */}
                        <p className="text-xs text-neutral-500 mt-0.5">Max {MAX_UPLOAD_MB} MB per file</p>
                      </div>
                      <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 text-xs font-medium rounded">
                        {formData.workbooks.length}/{fileLimit} files
                      </span>
                    </div>

                    {formData.workbooks.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {formData.workbooks.map((file) => (
                          <div key={file.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                            <div className="flex items-center gap-3">
                              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <div>
                                <p className="font-medium text-secondary-900 text-xs sm:text-sm">{file.name}</p>
                                <p className="text-xs text-neutral-500">
                                  {file.size && file.size > 0
                                    ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                                    : 'Size unknown'}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => removeWorkbook(file.id)} className="p-2 text-error hover:bg-red-50 rounded-lg transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-amber-800">
                        <span className="font-medium">Accepted formats:</span> PDF, PPT, PPTX only
                      </p>
                    </div>

                    <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        id="workbook-upload"
                        type="file"
                        multiple
                        accept=".pdf,.ppt,.pptx"
                        onChange={handleWorkbookUpload}
                        className="hidden"
                        disabled={formData.workbooks.length >= fileLimit}
                      />
                      <label htmlFor="workbook-upload" className="cursor-pointer">
                        <svg className="w-10 h-10 mx-auto text-neutral-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                        </svg>
                        <p className="text-xs sm:text-sm text-neutral-600 mb-1">
                          {formData.workbooks.length === 0 ? 'Click to upload or drag and drop' : 'Click to add more files'}
                        </p>
                        <p className="text-xs text-neutral-500">Max {fileLimit} files</p>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-neutral-200">
                    <Button variant="ghost" onClick={handleBack}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      }
                    >
                      Back
                    </Button>
                    <Button variant="primary-filled" size="lg" onClick={handleNext}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      }
                      iconPosition="right"
                    >
                      Next: Pre & Post Session
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Pre & Post Session Configuration (Phase 2) */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {/* Header (inline, no card) */}
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-1">
                  Pre & Post Session Features
                </h2>
                <p className="text-sm text-neutral-600">
                  Optional features to enhance learning before and after training. Skip this step if not needed.
                </p>
              </div>

              {/* Pre-Session card (blue tint) */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Enable Pre-Session</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants complete tasks before the training day
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.preSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, preSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.preSessionEnabled && (
                  <div className="pt-4 border-t border-blue-200 space-y-4">
                    {/* Materials upload */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-900 mb-2">Pre-Read Materials</label>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.ppt,.pptx,.doc,.docx,image/*,video/*"
                        onChange={(e) => {
                          const files = Array.from(e.target.files).map(f => ({
                            file: f,
                            name: f.name,
                            size: f.size,
                            type: f.type,
                          }));
                          setFormData({
                            ...formData,
                            preSessionMaterials: [...formData.preSessionMaterials, ...files],
                          });
                          e.target.value = '';
                        }}
                        className="block w-full text-sm text-neutral-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                      {formData.preSessionMaterials.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {formData.preSessionMaterials.map((m, idx) => (
                            <li key={idx} className="flex items-center justify-between text-sm bg-neutral-50 px-3 py-2 rounded">
                              <span className="truncate">{m.name}</span>
                              <button
                                type="button"
                                onClick={() => setFormData({
                                  ...formData,
                                  preSessionMaterials: formData.preSessionMaterials.filter((_, i) => i !== idx),
                                })}
                                className="text-red-500 hover:text-red-700 ml-2"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Self-assessment toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Self-Assessment</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.selfAssessmentEnabled}
                          onChange={(e) => setFormData({ ...formData, selfAssessmentEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>

                    {formData.selfAssessmentEnabled && (
                      <div className="pl-4 border-l-2 border-neutral-100 space-y-3">
                        {formData.selfAssessmentQuestions.map((q, idx) => (
                          <div key={idx} className="bg-neutral-50 p-3 rounded space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                placeholder="Question text"
                                value={q.questionText}
                                onChange={(e) => {
                                  const updated = [...formData.selfAssessmentQuestions];
                                  updated[idx] = { ...q, questionText: e.target.value };
                                  setFormData({ ...formData, selfAssessmentQuestions: updated });
                                }}
                                className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm"
                              />
                              <select
                                value={q.questionType}
                                onChange={(e) => {
                                  const updated = [...formData.selfAssessmentQuestions];
                                  updated[idx] = { ...q, questionType: e.target.value };
                                  setFormData({ ...formData, selfAssessmentQuestions: updated });
                                }}
                                className="px-3 py-2 border border-neutral-200 rounded text-sm"
                              >
                                <option value="rating">Rating (1-5)</option>
                                <option value="text">Text</option>
                                <option value="mcq">Multiple Choice</option>
                              </select>
                              <button
                                type="button"
                                onClick={() => setFormData({
                                  ...formData,
                                  selfAssessmentQuestions: formData.selfAssessmentQuestions.filter((_, i) => i !== idx),
                                })}
                                className="text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                            {q.questionType === 'mcq' && (
                              <input
                                type="text"
                                placeholder="Options (comma-separated)"
                                value={q.optionsText !== undefined ? q.optionsText : (q.options || []).join(', ')}
                                onChange={(e) => {
                                  const updated = [...formData.selfAssessmentQuestions];
                                  updated[idx] = { ...q, optionsText: e.target.value };
                                  setFormData({ ...formData, selfAssessmentQuestions: updated });
                                }}
                                className="w-full px-3 py-2 border border-neutral-200 rounded text-sm"
                              />
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({
                            ...formData,
                            selfAssessmentQuestions: [
                              ...formData.selfAssessmentQuestions,
                              { questionText: '', questionType: 'rating', options: [], optionsText: '' },
                            ],
                          })}
                        >
                          + Add Question
                        </Button>
                      </div>
                    )}

                    {/* Challenge capture */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Challenge Capture</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.challengeCaptureEnabled}
                          onChange={(e) => setFormData({ ...formData, challengeCaptureEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>

                    {/* Expectation capture */}
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Expectation Capture</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.expectationCaptureEnabled}
                          onChange={(e) => setFormData({ ...formData, expectationCaptureEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Post-Session card (green tint) */}
              <div className="bg-green-50 border border-green-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-secondary-900">Enable Post-Session Tracking</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants track action plans and reflect after training
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.postSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, postSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.postSessionEnabled && (
                  <div className="pt-4 border-t border-green-200 space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Action Plan Tracking</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.actionPlanTrackingEnabled}
                          onChange={(e) => setFormData({ ...formData, actionPlanTrackingEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Manager Reinforcement</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.managerReinforcementEnabled}
                          onChange={(e) => setFormData({ ...formData, managerReinforcementEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-secondary-900">Reflection Cycles</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.reflectionCyclesEnabled}
                          onChange={(e) => setFormData({ ...formData, reflectionCyclesEnabled: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>

                    {formData.reflectionCyclesEnabled && (
                      <div className="pl-4 border-l-2 border-green-200 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-secondary-900 mb-1">
                            Reflection Intervals (days after session)
                          </label>
                          <input
                            type="text"
                            value={reflectionIntervalsText}
                            onChange={(e) => {
                              // Show exactly what was typed; parse alongside it rather
                              // than feeding the parsed value back into the field.
                              setReflectionIntervalsText(e.target.value);
                              const intervals = e.target.value
                                .split(',')
                                .map(s => parseInt(s.trim(), 10))
                                .filter(n => Number.isFinite(n) && n > 0);
                              setFormData({ ...formData, reflectionIntervals: intervals });
                            }}
                            // Tidy up on exit: drops stray separators and whitespace
                            // without interfering mid-entry.
                            onBlur={() => setReflectionIntervalsText(formData.reflectionIntervals.join(', '))}
                            placeholder="30, 60, 90"
                            className="w-full px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                          />
                          <p className="text-xs text-neutral-500 mt-1">Comma-separated numbers. Default: 30, 60, 90.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-secondary-900 mb-2">
                            Reflection Questions
                          </label>
                          {/* Trainer-facing hint — explains the leave-blank-for-defaults
                              behavior so the trainer doesn't feel they MUST write
                              questions to enable reflections. */}
                          <div className="mb-3 rounded-lg bg-blue-50 ring-1 ring-inset ring-blue-200/70 px-3 py-2 text-xs text-blue-800">
                            <span className="font-medium">Tip:</span> Add your own reflection prompts below, or leave this empty to use the standard 4 questions —
                            <em className="not-italic"> "What action did you implement?", "What result did you observe?", "What challenge remains?", "What support do you need?"</em>
                          </div>
                          {formData.reflectionQuestions.map((q, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                value={q.questionText}
                                onChange={(e) => {
                                  const updated = [...formData.reflectionQuestions];
                                  updated[idx] = { ...q, questionText: e.target.value };
                                  setFormData({ ...formData, reflectionQuestions: updated });
                                }}
                                className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData({
                                  ...formData,
                                  reflectionQuestions: formData.reflectionQuestions.filter((_, i) => i !== idx),
                                })}
                                className="text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              reflectionQuestions: [
                                ...formData.reflectionQuestions,
                                { questionText: '', order: formData.reflectionQuestions.length },
                              ],
                            })}
                          >
                            + Add Question
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Info note */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-5 py-4 text-center">
                <p className="text-sm text-neutral-600">
                  Pre & Post session features are optional. You can skip this step and the session will work exactly as before.
                </p>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={handleBack}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  }
                >
                  Back
                </Button>
                <Button type="button" variant="primary-filled" size="lg" onClick={handleCreateSession}>
                  Create Session
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => navigate('/trainer/sessions')}
        title=""
        size="lg"
      >
        {createdSession && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-success rounded-full mb-4">
                <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold text-secondary-900">Session Created!</h3>
              <p className="text-neutral-600 mt-2">Your training session has been scheduled successfully</p>
            </div>

            <div className="border border-neutral-200 rounded-lg p-5 space-y-4">
              <div>
                <h4 className="text-base sm:text-lg font-medium text-secondary-900">{createdSession.name}</h4>
                <p className="text-sm text-neutral-600 mt-1">
                  {new Date(createdSession.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {createdSession.endDate && createdSession.endDate !== createdSession.startDate && (
                    <> - {new Date(createdSession.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">Session Code</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl sm:text-2xl font-semibold text-primary-600">{createdSession.sessionCode}</p>
                    <button
                      onClick={() => {
                        const code = createdSession.sessionCode;
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(code);
                        } else {
                          const textArea = document.createElement('textarea');
                          textArea.value = code;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                        }
                      }}
                      className="p-1.5 text-neutral-500 hover:text-secondary-900 hover:bg-neutral-100 rounded transition-colors"
                      title="Copy code"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-neutral-700 mb-2">QR Code</p>
                  <img src={createdSession.qrCode} alt="QR Code" className="w-24 h-24 border border-neutral-200 rounded-lg" />
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-200">
                <p className="text-sm font-medium text-neutral-700 mb-1">Capacity</p>
                <p className="text-sm text-secondary-900">{createdSession.maxParticipants} participants</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                className="flex-1"
                onClick={() => navigate('/trainer/sessions')}
              >
                Back to Sessions
              </Button>
              <Button
                variant="primary-filled"
                className="flex-1"
                onClick={() => navigate(`/trainer/sessions/${createdSession.id}/setup`)}
              >
                Setup Sub-sessions
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Unsaved Changes Confirmation */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={confirmLeave}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmText="Leave"
        cancelText="Stay"
        variant="warning"
      />
    </DashboardLayout>
  );
};

export default TrainerCreateSessionPage;
